import { CircleCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { ApiError, api, unwrapVoid } from "@/api/client";
import { Field, PasswordInput } from "@/components/common/Field";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { errorText, fieldError } from "@/lib/errors";

import { AuthShell, tokenFromHash } from "./AuthShell";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const token = tokenFromHash(hash);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<"form" | "done" | "invalid">(token ? "form" : "invalid");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const toLogin = (
    <Button asChild size="lg" className="w-full">
      <Link to="/login">{t("reset.toLogin")}</Link>
    </Button>
  );

  if (state === "invalid") {
    return (
      <AuthShell title={t("reset.invalidTitle")}>
        <div className="flex flex-col gap-5">
          <Alert variant="warning">{t("reset.invalidText")}</Alert>
          <Button asChild variant="secondary">
            <Link to="/forgot-password">{t("login.forgot")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }
  if (state === "done") {
    return (
      <AuthShell title={t("reset.doneTitle")}>
        <div className="flex flex-col items-center gap-5 text-center">
          <CircleCheck className="size-12 text-success" aria-hidden="true" />
          <p role="status">{t("reset.doneText")}</p>
          {toLogin}
        </div>
      </AuthShell>
    );
  }

  const mismatch = confirm !== "" && confirm !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      unwrapVoid(await api.POST("/api/v1/auth/password/reset", { body: { token, password } }));
      // Drop the token from the address bar and history.
      window.history.replaceState(null, "", window.location.pathname);
      setState("done");
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_token") setState("invalid");
      else setError(err);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthShell title={t("reset.title")} subtitle={t("reset.subtitle")}>
      <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
        <Field label={t("reset.password")} hint={t("password.hint")} error={fieldError(t, error, "password")}>
          <PasswordInput
            autoComplete="new-password"
            required
            autoFocus
            minLength={10}
            className="h-11"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label={t("password.confirm")} error={mismatch ? t("password.mismatch") : undefined}>
          <PasswordInput
            autoComplete="new-password"
            required
            className="h-11"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {error !== null && !fieldError(t, error, "password") && (
          <Alert variant="danger">{errorText(t, error)}</Alert>
        )}
        <Button type="submit" size="lg" disabled={pending || password.length < 10 || confirm !== password}>
          {t("reset.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
